import { Box, Button, Stepper, Step, StepLabel } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { quizNavigatorStyles } from './QuizNavigator.styles';

export type StepStatus = 'submitted' | 'unseen';

export interface QuizStep {
  id: number | string;
  status: StepStatus;
  current: boolean;
}

export interface QuizNavigatorProps {
  steps: QuizStep[];
  onPrevious: () => void;
  onNext: () => void;
  onStepClick?: (id: number | string) => void;
  disablePrevious?: boolean;
  disableNext?: boolean;
  nextLabel?: string;
  lastSeenIndex?: number;
}

export const QuizNavigator = ({
  steps,
  onPrevious,
  onNext,
  onStepClick,
  disablePrevious = false,
  disableNext = false,
  nextLabel = 'Next',
  lastSeenIndex = 0,
}: QuizNavigatorProps) => {
  const theme = useTheme();
  const activeStepIndex = steps.findIndex((step) => step.current);
  const isFirstStep = activeStepIndex <= 0;
  const previousDisabled = disablePrevious || isFirstStep;
  const nextDisabled = disableNext;

  return (
    <Box sx={quizNavigatorStyles.container(theme)}>
      <Box sx={quizNavigatorStyles.previousButton(theme)}>
        <Button
          variant="contained"
          onClick={onPrevious}
          disabled={previousDisabled}
          aria-label="Go to previous question"
          sx={quizNavigatorStyles.button(theme)}
        >
          Previous
        </Button>
      </Box>
      <Box sx={quizNavigatorStyles.stepper(theme, lastSeenIndex, steps.length)}>
        <Stepper activeStep={activeStepIndex} alternativeLabel>
          {steps.map((step, index) => {
            const isSkipped = step.status !== 'submitted' && index < lastSeenIndex;

            return (
              <Step
                key={step.id}
                completed={false}
                onClick={() => onStepClick?.(step.id)}
                onKeyDown={(e) => {
                  if (onStepClick && (e.key === 'Enter' || e.key === ' ')) {
                    e.preventDefault();
                    onStepClick(step.id);
                  }
                }}
                tabIndex={onStepClick ? 0 : -1}
                role={onStepClick ? 'button' : undefined}
                aria-label={`Question ${index + 1}${step.current ? ', current' : ''}${step.status === 'submitted' ? ', answered' : ', not answered'}`}
                aria-current={step.current ? 'step' : undefined}
                sx={quizNavigatorStyles.step(theme, !!onStepClick)}
              >
                <StepLabel
                  sx={quizNavigatorStyles.stepLabel(theme, step.status, step.current, isSkipped)}
                />
              </Step>
            );
          })}
        </Stepper>
      </Box>
      <Box sx={quizNavigatorStyles.nextButton(theme)}>
        <Button
          variant="contained"
          onClick={onNext}
          disabled={nextDisabled}
          aria-label={nextLabel === 'End Test' ? 'End test' : 'Go to next question'}
          sx={quizNavigatorStyles.button(theme)}
        >
          {nextLabel}
        </Button>
      </Box>
    </Box>
  );
};

export default QuizNavigator;
